import React, { ReactElement, useEffect } from 'react';
import { useRouter } from 'next/router';
import useSWR from 'swr';
import { ClassNames, css } from '@emotion/react';
import styled from '@emotion/styled';
import { stringify } from 'querystring';

import { usePagedInfiniteList, VirtualizedListChildProps } from '@bucketplace/ui';
import { Product } from '@bucketplace/common';
import { MEDIA_QUERY } from '@bucketplace/design-system';
import { useExperimentType } from '@bucketplace/xpc-sdk';

import { getSectionItems } from 'promotions/api/promotion';
import { VirtualizedList } from 'components/VirtualizedList';
import { PlaceholderAnim } from 'components/PlaceholderAnim';
import { ProductCardSectionItem } from 'components/Product/ProductCardSectionItem';
import { useUserContext } from 'provider';

const LIST_SIZE = 20;

export function getSectionItemsKey (id: string, detailId: string, page: number, isLoggedIn: boolean): string {
  return `/api/promotions/${id}/section-items?${stringify({
    detailId,
    page,
    isLoggedIn,
  })}`;
}

export interface SectionStoreItemsProps {
  id: string;
  detailId: string;
}

export function SectionStoreItems ({
  id,
  detailId,
}: SectionStoreItemsProps): ReactElement {
  const router = useRouter();
  const tabId = router.asPath;
  const { userInfo } = useUserContext();
  const isLoggedIn = !!userInfo?.user;

  const { experimentType: storeItemsExperimentType = 'A' } = useExperimentType({
    experimentKey: 1950,
    type: 'TC',
  });

  const { data, error } = useSWR(
    getSectionItemsKey(id, detailId, 0, isLoggedIn),
    () => getSectionItems(id, detailId, {
      page: 0,
      isLoggedIn,
    })
  );

  const pending = (!data && !error) || !userInfo;

  const {
    count,
    getItem,
    handleRowsRendered,
    reloadData,
  } = usePagedInfiniteList(
    tabId,
    pending,
    async (page) => {
      if (storeItemsExperimentType === 'B') {
        return {
          page,
          items: [],
          hasNext: false,
        };
      }

      const nextData = await getSectionItems(id, detailId, {
        page,
        isLoggedIn,
      });

      return {
        page,
        items: nextData.sectionItems ?? [],
        hasNext: nextData.hasNext ?? false,
      };
    },
    () => (data
      ? {
        page: 0,
        offset: 0,
        items: storeItemsExperimentType === 'B' ? [] : (data.sectionItems ?? []),
        hasNext: storeItemsExperimentType === 'B' ? false : (data.hasNext ?? false),
      }
      : null
    ),
    LIST_SIZE
  );

  useEffect(() => {
    reloadData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn]);

  const renderItem = ({ index }: VirtualizedListChildProps): React.ReactNode => {
    const item = getItem(index)?.item as Product;
    if (!item) {
      return (
        <PlaceholderAnim />
      );
    }
    return (
      <ProductCardSectionItem
        product={item}
        logData={{
          section: '기획전 디테일',
          index,
        }}
      />
    );
  };

  return (
    <Wrapper>
      { !pending && (
        <ClassNames>
          { ({ css }) => (
            <VirtualizedList
              tabId={tabId}
              gap={{
                mobile: '0 16',
                tablet: '30 16',
                laptop: '30 20',
              }}
              column={{ mobile: 2, tablet: 2, laptop: 3 }}
              className={css`
              ${MEDIA_QUERY.max('mobile', `
                  padding: 0 8px;
                `)}
              `}
              innerClassName={css`
                > article {
                  padding: 16px 0px;
                }
                &:last-of-type {
                  > article {
                    border: 0;
                  }
                }
              `}
              overscanHeight={1000}
              count={count}
              isLoaded={(index) => getItem(index) != null}
              onRowsRendered={handleRowsRendered}
              renderItem={renderItem}
            />
          ) }
        </ClassNames>
      ) }
    </Wrapper>
  );
}

const Wrapper = styled.div`
  ${MEDIA_QUERY.max('mobile', css`
    padding: 0 8px;
  `)}
`;
